import type React from 'react'
import { BILLING_PLAN_NAMES, type BillingStatus } from '../../types'
import type { CurrentBill as CurrentBillData } from '../../types/billingStatement'
import { formatCents, overageSummary } from '../../utils/billingStanding'
import { colors, fontSize, radius, shadow, spacing } from '../../styles/theme'
import UsageForecastChart from './UsageForecastChart'

/**
 * The Billing page's "This month": spend so far and the estimated period-end spend as the headline, a chart
 * of pages so far with a forecast to the period end, then one card per figure of the plan.
 *
 * Money comes only from the current bill (the server prices it with the month-end calculation), so this
 * never prices anything itself. Members without Manage billing get no current bill: they see usage, not money.
 */

const section: React.CSSProperties = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.lg,
  boxShadow: shadow.sm,
  padding: spacing.xl,
  marginBottom: spacing.lg,
}

const tile: React.CSSProperties = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  padding: spacing.md,
  minWidth: 0,
}

const grid = (min: number): React.CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fit, minmax(min(${min}px, 100%), 1fr))`,
  gap: spacing.md,
})

const caption: React.CSSProperties = {
  fontSize: fontSize.xs, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em',
}

function lastDay(endIso: string): string {
  const [y, m, d] = endIso.slice(0, 10).split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d - 1)).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

function monthName(iso: string): string {
  const [y, m] = iso.slice(0, 10).split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

function Hero({ testId, label, value, note, emphasis }: { testId: string; label: string; value: string; note: string; emphasis?: boolean }) {
  return (
    <div data-testid={testId} style={{
      ...tile, padding: spacing.lg,
      background: emphasis ? colors.primaryLight : colors.surface,
      border: `${emphasis ? 2 : 1}px solid ${emphasis ? colors.primary : colors.border}`,
    }}>
      <div style={caption}>{label}</div>
      <div style={{ fontSize: fontSize['5xl'], fontWeight: 700, lineHeight: 1.1, margin: `${spacing.xs} 0`, color: colors.text, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      <div style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>{note}</div>
    </div>
  )
}

function Stat({ label, value, note, testId }: { label: string; value: string; note?: string; testId?: string }) {
  return (
    <div style={tile} data-testid={testId ?? `stat-${label}`}>
      <div style={caption}>{label}</div>
      <div style={{ fontSize: fontSize.xl, fontWeight: 700, color: colors.text, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {note && <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginTop: 2 }}>{note}</div>}
    </div>
  )
}

/** The two headline figures, from the current bill's disposition. */
function spend(bill: CurrentBillData): { toDate: [string, string]; estimate: [string, string] } {
  const end = lastDay(bill.periodEnd)
  if (bill.noCharge) {
    return { toDate: ['$0.00', 'No charge for this team.'], estimate: ['$0.00', 'No charge for this team.'] }
  }
  if (bill.disposition === 'trial') {
    const starts = bill.trialBillingStarts ? `Billing starts with ${monthName(bill.trialBillingStarts)}.` : 'Billing starts after the trial.'
    return { toDate: ['$0.00', 'Free trial — this month isn\'t billed.'], estimate: ['$0.00', starts] }
  }
  if (bill.disposition === 'refused') {
    return { toDate: ['—', 'This month can\'t be priced yet; see the current bill below.'], estimate: ['—', 'Not available until the month can be priced.'] }
  }
  return {
    toDate: [formatCents(bill.totalCents), `Since ${new Date(`${bill.periodStart.slice(0, 10)}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })}, billed ${new Date(`${bill.billsOn.slice(0, 10)}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })}.`],
    estimate: bill.projection
      ? [formatCents(bill.projection.projectedTotalCents), `If usage keeps its pace: about ${bill.projection.projectedPages.toLocaleString()} pages by ${end}.`]
      : ['—', 'Too early in the month to estimate.'],
  }
}

export default function ThisMonth({ status, bill, billLoading }: {
  status: BillingStatus
  /** Null while loading, or when the viewer may not see money. */
  bill: CurrentBillData | null
  billLoading: boolean
}) {
  // Free counts documents; every paid plan counts pages.
  const unit: 'page' | 'document' = status.includedPages != null ? 'page' : 'document'
  const included = status.includedPages ?? status.includedDocuments ?? null
  const used = unit === 'page' ? status.pagesThisPeriod : status.usedThisPeriod
  const includedLeft = included != null ? Math.max(0, included - used) : null
  const planName = BILLING_PLAN_NAMES[status.planCode] ?? 'Standard'
  const isFree = status.planCode === 'free'
  const overage = overageSummary(status, status.appliesOverageSettings, status.perPageCents, status.includedPages)
  const money = bill ? spend(bill) : null

  return (
    <div style={section} data-testid="this-month">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: spacing.sm, marginBottom: spacing.md, flexWrap: 'wrap' }}>
        <div style={{ fontSize: fontSize.lg, fontWeight: 600 }}>This month</div>
        <div style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>· {planName} plan</div>
        {bill && (
          <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginLeft: 'auto' }}>
            {monthName(bill.periodStart)} · ends {lastDay(bill.periodEnd)}
          </div>
        )}
      </div>

      {money && (
        <div style={{ ...grid(220), marginBottom: spacing.lg }}>
          <Hero testId="spend-to-date" label="Spend so far" value={money.toDate[0]} note={money.toDate[1]} />
          <Hero testId="spend-estimate" label="Estimated at period end" value={money.estimate[0]} note={money.estimate[1]} emphasis />
        </div>
      )}
      {!money && billLoading && (
        <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.md }}>Loading spend…</div>
      )}

      {bill && (
        <div style={{ marginBottom: spacing.lg }}>
          <div style={{ fontSize: fontSize.sm, fontWeight: 600, marginBottom: spacing.sm }}>Pages this period</div>
          <UsageForecastChart periodStart={bill.periodStart} periodEnd={bill.periodEnd} daily={bill.daily ?? []}
            included={bill.includedPages} projectedPages={bill.projection?.projectedPages ?? null} usageFrom={bill.usageFrom} />
        </div>
      )}

      <div style={grid(150)}>
        <Stat label={unit === 'page' ? 'Pages rendered' : 'Documents rendered'} value={used.toLocaleString()} />
        {unit === 'page' && <Stat label="Documents" value={status.usedThisPeriod.toLocaleString()} />}
        {included != null && (
          <Stat label={isFree ? 'Allowance left' : 'Included left'} value={includedLeft!.toLocaleString()}
            note={`of ${included.toLocaleString()} ${unit}s${isFree ? ' (for information)' : ''}`} />
        )}
        {!isFree && status.perPageCents != null && (
          <Stat label="Beyond included" value={status.stopsAtIncludedPages ? 'Refused' : formatCents(status.perPageCents)}
            note={status.stopsAtIncludedPages ? 'Overage is off' : 'per page'} />
        )}
        {status.appliesOverageSettings && status.allowOverage !== false && (
          <Stat label="Overage limit" value={status.overageLimitDollars == null ? 'No limit' : `$${status.overageLimitDollars.toLocaleString()}`}
            note={status.overageLimitDollars == null ? undefined : 'per month'} />
        )}
        {!isFree && status.monthlyMinimumCents != null && (
          <Stat label="Monthly minimum" value={formatCents(status.monthlyMinimumCents)}
            note={included != null ? `includes ${included.toLocaleString()} ${unit}s` : undefined} />
        )}
        {status.concurrentRenderLimit != null && (
          <Stat label="Renders at once" value={status.concurrentRenderLimit.toLocaleString()} note="documents in parallel" />
        )}
      </div>

      {isFree && (
        <div data-testid="free-no-charge" style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginTop: spacing.md }}>
          No charge — your team's plan is assigned by DocMorphia.
        </div>
      )}
      {overage && (
        <div data-testid="plan-overage" style={{ fontSize: fontSize.sm, color: status.stopsAtIncludedPages ? colors.warning : colors.textSecondary, marginTop: spacing.md }}>
          {overage}
        </div>
      )}
    </div>
  )
}
